import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import {Label} from "react-foundation";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import AirflowDagLogs from "../Components/airflow/dagLogs/AirflowDagLogs";
import ProjectionsUpload from "./Components/ProjectionsUpload";
import VersionSelect from "./Components/VersionSelect";
import UploadTemplateHelp from "./Components/UploadTemplateHelp";
import {uploadTemplates} from "./uploadTemplates";
import {invokeDAGviaApiPost} from "../../Api/invokeDag";
import {toastError, toastSuccess} from "../../utils/toast";
import {ACTIVE_DAG_RUN_STATUSES} from "../Components/airflow/dagLogs/dagLogUtils";
import StoreChangeFeedbackModal, {STORE_CHANGE_FEEDBACK_DAGS} from "./Components/StoreChangeFeedbackModal";

const hasChangeFeedback = (run) => STORE_CHANGE_FEEDBACK_DAGS.includes(run.process)
    && !ACTIVE_DAG_RUN_STATUSES.includes(String(run.status ?? '').toLowerCase());

const ProjectionStoresUpload = () => {
    const [loadingCSV, setLoadingCSV] = useState(false)
    const [fileType, setFileType] = useState(undefined);
    const [version, setVersion] = useState("");
    const [fileTypeConfigs, setFileTypeConfigs] = useState({});
    const [dagLogsRefreshTrigger, setDagLogsRefreshTrigger] = useState(undefined);
    const [feedbackRun, setFeedbackRun] = useState(null);

    useEffect(() => {
        setFileTypeConfigs(
            {
                csg: {
                    endpoint: 'uploadCSG',
                    dag: 'edp_projection_csg_stores',
                    filePath: `gs://${process.env.REACT_APP_GCS_DATA_BASE_URL}/p_data=chain_store_guide/p_version=${version}/`,
                    outputPath: `gs://${process.env.REACT_APP_GCS_SERVICES_BASE_URL}projectionStores/p_version=${version}/chain_store_guide.parquet`
                },
                infra: {
                    endpoint: 'uploadInfra',
                    dag: 'edp_projection_infra_stores',
                    filePath: `gs://${process.env.REACT_APP_GCS_DATA_BASE_URL}/p_data=infra/p_version=${version}/`,
                    outputPath: `gs://${process.env.REACT_APP_GCS_SERVICES_BASE_URL}projectionStores/p_version=${version}/infra.parquet`
                },
                manual: {
                    endpoint: 'uploadManual',
                    dag: 'edp_projection_manual_stores',
                    filePath: `gs://${process.env.REACT_APP_GCS_DATA_BASE_URL}/p_data=manual/p_version=${version}/`,
                    outputPath: `gs://${process.env.REACT_APP_GCS_SERVICES_BASE_URL}projectionStores/p_version=${version}/manual.parquet`
                },
                c_and_s: {
                    endpoint: 'uploadCAndS',
                    dag: 'edp_projection_c_and_s_stores',
                    filePath: `gs://${process.env.REACT_APP_GCS_DATA_BASE_URL}/p_data=c_and_s/p_version=${version}/`,
                    outputPath: `gs://${process.env.REACT_APP_GCS_SERVICES_BASE_URL}projectionStores/p_version=${version}/c_and_s.parquet`
                },
                store_service: {
                    dag: 'edp_projection_spins_stores',
                }
            }
        )
    }, [version])

    const isDagOnly = fileType && !fileTypeConfigs[fileType].endpoint;

    const triggerStoreServiceDag = () => {
        const dagName = fileTypeConfigs[fileType].dag;
        setLoadingCSV(true);
        invokeDAGviaApiPost(dagName, {
            version: version.trim(),
        })
            .then((response) => {
                setLoadingCSV(false);
                if (response?.apiError)
                    toastError(toast, `${dagName} DAG trigger failed with error ${JSON.stringify(response.apiError)}`)
                else {
                    toastSuccess(toast, `${dagName} DAG triggered successfully with response ${JSON.stringify(response)}`)
                    setDagLogsRefreshTrigger(Date.now())
                }
            })
            .catch(e => {
                setLoadingCSV(false);
                toastError(toast, `${dagName} DAG trigger failed with error ${JSON.stringify(e)}`)
            })
    }

    const fileTypeOptions = [
        {label: 'Spins Store Service', value: 'store_service'},
        {label: 'Chain Store Guide', value: 'csg'},
        {label: 'C and S', value: 'c_and_s'},
        {label: 'Infra', value: 'infra'},
        {label: 'Manual', value: 'manual'},
    ]

    return (
        <>
            {!loadingCSV &&
                <div className={'projection-upload-trigger-dag-page-controls'}>
                        <div className={'projection-upload-select-container'}>
                            <SelectDropdown id={'fileTypeSelect_dropdown'}
                                            inputLabel={"File Type"}
                                            options={fileTypeOptions}
                                            onChange={(e) => setFileType(e.value)}
                                            selectValue={fileType}
                                            selectLabel={fileTypeOptions?.find(option => option.value === fileType)?.label}
                            />
                        </div>
                        <div className={'projection-upload-select-container'}>
                            <VersionSelect version={version} setVersion={setVersion} />
                        </div>
                        {isDagOnly
                            ? <div className={'upload-button-container'}>
                                <Label className={"csv-label"} title={"Ingest Store Service Data"}>
                                    <button type={'button'}
                                            className={`hollow button secondary ${(!fileType || !version.trim()) ? 'disabled' : ''}`}
                                            disabled={!fileType || !version.trim()}
                                            onClick={triggerStoreServiceDag}>
                                        Ingest Store Service Data
                                    </button>
                                </Label>
                              </div>
                            : <ProjectionsUpload
                                getUploadUrl={() => `${process.env.REACT_APP_EDP_API_BASE_URL}/${fileTypeConfigs[fileType]?.endpoint}`}
                                getUploadAdditionalData={() => ({version: version.trim()})}
                                getDagName={() => fileTypeConfigs[fileType].dag}
                                getDagRunConf={(file) => ({
                                    input_filepath: fileTypeConfigs[fileType].filePath,
                                    input_filename: file.name,
                                    output_path: fileTypeConfigs[fileType].outputPath,
                                    version: version.trim(),
                                })}
                                setLoading={setLoadingCSV}
                                setDisabled={!fileType || !version.trim()}
                                onDagTriggered={() => setDagLogsRefreshTrigger(Date.now())}
                            />
                        }
                        <UploadTemplateHelp template={uploadTemplates[fileType]}/>
                </div>
            }
            <div className="sld-dag-airflowlogs-container" data-testid="airflow-logs-container">
                <h5>DAG Run Logs</h5>
                <AirflowDagLogs includeDagName={true}
                                includeVersion={true}
                                setAutoRefresh={true}
                                refreshTrigger={dagLogsRefreshTrigger}
                                processName={Object.values(fileTypeConfigs).map(x => x.dag)}
                                customColumns={[
                                    {
                                        header: 'Change Feedback',
                                        accessorKey: 'runid',
                                        id: 'changeFeedback',
                                        enableColumnFilter: false,
                                        cell: ({row}) => hasChangeFeedback(row.original)
                                            ? <div className="open-json-payload-modal">
                                                <button type="button"
                                                        className="button tertiary"
                                                        data-testid={`change-feedback-button-${row.original.runid}`}
                                                        onClick={() => setFeedbackRun({
                                                            dagId: row.original.process,
                                                            runId: row.original.runid
                                                        })}>View</button>
                                            </div>
                                            : '—'
                                    }
                                ]}/>
            </div>
            {feedbackRun &&
                <StoreChangeFeedbackModal dagId={feedbackRun.dagId}
                                          runId={feedbackRun.runId}
                                          onClose={() => setFeedbackRun(null)}/>
            }
        </>

    )
}

export default ProjectionStoresUpload;